import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { PAID_BREAK_BEHAVIORS } from '@careerstart/wae-common/schema/models/position-template/enums/paidBreakBehaviors';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';

import asyncService from '../../../../../datahub/asyncService';
import SearchBarSingle from '../../../../../main/components/SearchBarV3/SearchBarSingle';
import selectUser from '../../../../store/selectors/appSelector';
import { BLACK, PRIMARY_PURPLE } from '../../../../theme/colorConstants';
import { showSnackbar } from '../../../app';
import { updatePaidBreakBehavior } from '../reducer';

const dropdownSx = {
  '& .MuiInputLabel-root': { color: BLACK[0] },
  '& .MuiInputLabel-root.Mui-error': { color: BLACK[0] },
  '& .MuiOutlinedInput-root': {
    color: BLACK[0],
    '& .MuiOutlinedInput-notchedOutline': { borderColor: BLACK[70] },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BLACK[50] },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY_PURPLE[70] },
  },
};

const dropdownPaperSx = {
  backgroundColor: BLACK[100],
  color: BLACK[0],
};

const dropdownGridSx = {
  backgroundColor: 'transparent',
};

const LegalInfo = ({ positionTemplateId, paidBreakBehavior }) => {
  const dispatch = useDispatch();
  const { token, refreshToken } = useSelector(selectUser);

  const expected30MinLabel = LanguageConverterV2(
    'pt.legalInfo.paidBreakBehavior.expected30MinAfter6h'
  );
  const notExpectedLabel = LanguageConverterV2('pt.legalInfo.paidBreakBehavior.notExpected');
  const placeholderLabel = LanguageConverterV2('pt.legalInfo.paidBreakBehavior');

  const paidBreakOptions = useMemo(
    () => [
      { _id: PAID_BREAK_BEHAVIORS.EXPECTED_30_MIN_AFTER_6H, name: expected30MinLabel },
      { _id: PAID_BREAK_BEHAVIORS.NOT_EXPECTED, name: notExpectedLabel },
    ],
    [expected30MinLabel, notExpectedLabel]
  );

  const toOption = useCallback(
    (enumValue) => paidBreakOptions.find((opt) => opt._id === enumValue) || null,
    [paidBreakOptions]
  );

  const [selectedValue, setSelectedValue] = useState(paidBreakBehavior ?? null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSelectedValue(paidBreakBehavior ?? null);
  }, [paidBreakBehavior]);

  const handleChange = useCallback(
    (newOption) => {
      const newEnumValue = newOption?._id;
      if (!newEnumValue || newEnumValue === selectedValue) return;

      const previousValue = selectedValue;
      setSelectedValue(newEnumValue);
      setIsLoading(true);

      asyncService({
        httpMethod: 'POST',
        route: 'position-templates/paid-break-behavior/update',
        data: { positionTemplateId, paidBreakBehavior: newEnumValue },
        token,
        refreshToken,
        dispatch,
        onSuccess: () => {
          setIsLoading(false);
          dispatch(
            updatePaidBreakBehavior({ positionTemplateId, paidBreakBehavior: newEnumValue })
          );
          dispatch(showSnackbar({ message: 'success.position.template.update' }));
        },
        onError: () => {
          setIsLoading(false);
          setSelectedValue(previousValue);
          dispatch(showSnackbar({ message: 'error.positionTemplate.failUpdate' }));
        },
      });
    },
    [selectedValue, positionTemplateId, token, refreshToken, dispatch]
  );

  const input = useMemo(
    () => ({
      value: toOption(selectedValue),
      onChange: handleChange,
      name: 'paidBreakBehavior',
    }),
    [selectedValue, handleChange, toOption]
  );

  const meta = useMemo(() => ({}), []);

  return (
    <SearchBarSingle
      testId="paid-break-behavior-dropdown"
      dropDownOptions={paidBreakOptions}
      input={input}
      meta={meta}
      placeholder={placeholderLabel}
      optionDisplayField="name"
      fullWidth
      disabled={isLoading}
      sx={dropdownSx}
      paperSx={dropdownPaperSx}
      gridSx={dropdownGridSx}
      customIconColor={BLACK[0]}
    />
  );
};

LegalInfo.propTypes = {
  positionTemplateId: PropTypes.string.isRequired,
  paidBreakBehavior: PropTypes.string,
};

export default LegalInfo;
